import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Package, X, ShoppingBag, Check } from 'lucide-react';
import axios from 'axios';
import LazyImage from '../components/LazyImage';
import { useCart } from '../contexts/CartContext';
import { toast } from 'sonner';
import SEOHead from '../components/SEOHead';
import BreadcrumbSchema from '../components/BreadcrumbSchema';

const API = `${window.location.origin}/api`;

export default function BundlePage() {
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState([]);
  const [config, setConfig] = useState({ bundle_size: 3, discount_percent: 15 });
  const [filter, setFilter] = useState('All');
  const { addToCart } = useCart();

  useEffect(() => {
    axios.get(`${API}/products`).then(res => setProducts(res.data)).catch(console.error);
    axios.get(`${API}/settings`).then(res => {
      if (res.data.bundle_config) setConfig(res.data.bundle_config);
    }).catch(console.error);
  }, []);

  const toggleProduct = (product) => {
    if (selected.find(p => p.id === product.id)) {
      setSelected(selected.filter(p => p.id !== product.id));
    } else if (selected.length < config.bundle_size) {
      setSelected([...selected, product]);
    } else {
      toast.error(`You can only pick ${config.bundle_size} scents`);
    }
  };

  const originalTotal = selected.reduce((sum, p) => sum + (p.base_price || 0), 0);
  const discount = originalTotal * (config.discount_percent / 100);
  const bundleTotal = originalTotal - discount;
  const isComplete = selected.length === config.bundle_size;

  const addBundleToCart = () => {
    selected.forEach(product => {
      addToCart({
        ...product,
        quantity: 1,
        price: product.base_price * (1 - config.discount_percent / 100),
        variant: { size: 'Bundle', type: `Bundle ${config.discount_percent}% Off` },
      });
    });
    toast.success('Bundle added to cart!');
    setSelected([]);
  };

  const filtered = filter === 'All' ? products : products.filter(p => p.gender === filter);

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-8 pb-20">
      <SEOHead
        title="Build Your Bundle"
        description="Mix and match 3 luxury EDP scents (30% oil, Extrait-level) and save 15%. Build your personal fragrance bundle from JS Fragrance — Filipino-made, hand-blended."
        url="https://jsfragrances.com/bundle"
      />
      <BreadcrumbSchema crumbs={[{ name: 'Home', path: '/' }, { name: 'Bundle Builder', path: '/bundle' }]} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <Package className="w-8 h-8 text-[#D4AF37] mx-auto mb-3" />
          <h1 className="text-4xl sm:text-5xl font-light text-white mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {config.title || 'Build Your Bundle'}
          </h1>
          <p className="text-white/50 text-sm font-sans">{config.subtitle || `Pick ${config.bundle_size} scents and save ${config.discount_percent}%`}</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Product Grid */}
          <div className="flex-1">
            {/* Filter */}
            <div className="flex gap-2 mb-6 overflow-x-auto">
              {['All', 'Men', 'Women', 'Unisex'].map(f => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-4 py-1.5 text-xs uppercase tracking-[0.15em] font-sans whitespace-nowrap transition-colors ${
                    filter === f ? 'bg-[#D4AF37] text-black' : 'border border-white/10 text-white/50 hover:text-white'
                  }`}
                  data-testid={`bundle-filter-${f.toLowerCase()}`}
                >
                  {f}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {filtered.map(product => {
                const isSelected = selected.find(p => p.id === product.id);
                return (
                  <button
                    key={product.id}
                    onClick={() => toggleProduct(product)}
                    className={`text-left group relative transition-all duration-200 ${
                      isSelected ? 'ring-2 ring-[#D4AF37]' : 'hover:ring-1 hover:ring-white/20'
                    }`}
                    data-testid={`bundle-product-${product.id}`}
                  >
                    <div className="aspect-[3/4] bg-[#141414] overflow-hidden relative">
                      <LazyImage src={product.image_url} alt={product.name} className="w-full h-full" />
                      {isSelected && (
                        <div className="absolute top-2 right-2 w-6 h-6 bg-[#D4AF37] flex items-center justify-center">
                          <Check className="w-4 h-4 text-black" />
                        </div>
                      )}
                    </div>
                    <div className="p-2">
                      <p className="text-white text-xs font-sans truncate">{product.name}</p>
                      <p className="text-white/40 text-xs font-sans">₱{product.base_price?.toLocaleString()}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Bundle Summary Sidebar */}
          <div className="lg:w-80 flex-shrink-0">
            <div className="sticky top-24 bg-[#141414] border border-white/5 p-5">
              <h3 className="text-sm uppercase tracking-[0.2em] text-[#D4AF37] font-sans mb-4">Your Bundle</h3>

              {/* Selected Slots */}
              <div className="space-y-3 mb-6">
                {Array.from({ length: config.bundle_size }).map((_, idx) => {
                  const item = selected[idx];
                  return (
                    <div key={idx} className={`flex items-center gap-3 p-2 border ${item ? 'border-[#D4AF37]/30' : 'border-white/5 border-dashed'}`}>
                      {item ? (
                        <>
                          <div className="w-12 h-12 bg-[#0A0A0A] overflow-hidden flex-shrink-0">
                            <img src={item.image_url} alt="" className="w-full h-full object-cover" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-xs font-sans truncate">{item.name}</p>
                            <p className="text-white/40 text-xs font-sans">₱{item.base_price?.toLocaleString()}</p>
                          </div>
                          <button onClick={() => setSelected(selected.filter(p => p.id !== item.id))} className="text-white/30 hover:text-white">
                            <X className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <p className="text-white/20 text-xs font-sans flex-1 text-center">Pick scent #{idx + 1}</p>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Price Summary */}
              {selected.length > 0 && (
                <div className="space-y-2 border-t border-white/5 pt-4 mb-5">
                  <div className="flex justify-between text-sm font-sans">
                    <span className="text-white/40">Subtotal</span>
                    <span className="text-white/40 line-through">₱{originalTotal.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm font-sans">
                    <span className="text-[#D4AF37]">Bundle Discount ({config.discount_percent}%)</span>
                    <span className="text-[#D4AF37]">-₱{discount.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-base font-sans font-medium border-t border-white/5 pt-2">
                    <span className="text-white">Total</span>
                    <span className="text-[#D4AF37]">₱{bundleTotal.toLocaleString()}</span>
                  </div>
                </div>
              )}

              <button
                onClick={addBundleToCart}
                disabled={!isComplete}
                className={`w-full flex items-center justify-center gap-2 py-3 text-sm uppercase tracking-[0.15em] font-sans font-medium transition-colors ${
                  isComplete ? 'bg-[#D4AF37] text-black hover:bg-[#F3E5AB]' : 'bg-white/5 text-white/20 cursor-not-allowed'
                }`}
                data-testid="add-bundle-to-cart"
              >
                <ShoppingBag className="w-4 h-4" />
                {isComplete ? 'Add Bundle to Cart' : `Select ${config.bundle_size - selected.length} more`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
